import React from 'react';
import PropTypes from 'prop-types';
import { NotificationManager } from 'react-notifications';
import { steps } from '.';
import {
  QuoteAttachmentPoint,
  QuoteProduct,
  quoteCreationType,
  CreateQuoteStep1
} from './otherQuoteEntities';
import QuoteAttachmentPointCouplerInformation from './quoteAttachmentPointCouplerInformation';
import {
  QuoteInstallationInformation,
  QuoteInstallationQuery,
} from '../quotes/quoteInstallationInformation';
import { ProductConfiguration } from '../products/productConfiguration';
import { DealerBranch } from '../dealer-branches/dealerBranch';
import { Quote } from './quote';
import { QuoteProductConfiguration } from './quoteProductConfiguration';
import { withRouter } from 'react-router-dom';
import { stringify as toQueryString } from 'query-string';
import { appConfig as config } from '@ami/react/core';
import QuoteWizardComponent from './QuoteWizardComponent';
import { quoteWizardMode } from '../quotes/quoteWizardMode';
import { Machine } from '../machines/machine';
import { MachineFilter } from '../machines/machineFilter';
import { DealerBranchFilter } from '../dealer-branches/dealerBranchFilter';
import { SharedDocument } from '../documents/sharedDocument';
import SharedDocumentForQuoteFilter from '../documents/sharedDocumentForqQuoteFilter';
import { OemThumbMountType } from '../machines/oemThumbMountType';
import quotePricingService from './quotePricingService';
import { Spinner } from '@ami/react/core';

const QuoteWizardContainer = withRouter(
  class QuoteWizardContainer extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        quote: null,
        dealerBranch: null,
        machines: [],
        contactData: null,
        preventPrompt: false,
        step3SelectionInProgress: false,
        productConfiguration: null,
        quoteProductConfiguration: null,
        submitting: false,
        preparingForReview: false,
        sharedDocuments: [],
        documentIdDownloading: null,
        optInDiscounts: [],
        showPricingSpinner: false,
        showReviewAlert: false,
        copiedQuote: null,
        context: {
          showAmiOperations:
            config.isManagementApp ||
            (config.isDealerApp &&
              this.props.mode === quoteWizardMode.editMode),
          canPerformAmiOperations: config.isManagementApp,
          showCustomLineItems: () => {
            if (!this.state.context.showAmiOperations) return false;
            return (
              this.state.quote.customLineItems.length > 0 || !config.isDealerApp
            );
          },
          onQuoteProductConfigurationUpdate: (quoteProductConfiguration) =>
            this.setState({ quoteProductConfiguration }),
          onQuoteUpdate: (quote) => this.setState({ quote }),
          goBackToStep2: () => this.cancelStep3(),
          getQuoteDealerBranch: () => this.state.dealerBranch,
          getQuoteCarrierMachines: () => this.state.machines,
          setQuoteCarrierMachines: (machines) => this.setState({ machines }),
        },
        isLoading: true
      };
    }

    static propTypes = {
      //mode: PropTypes.oneOf(Object.values(quoteWizardMode)).isRequired // Not supported in IE11
      mode: PropTypes.oneOf(
        Object.keys(quoteWizardMode).map((item) => quoteWizardMode[item])
      ).isRequired,
    };

    async componentDidMount() {
      const productConfiguration = await ProductConfiguration.get();
      if (this.props.mode === quoteWizardMode.editMode) {
        const quote = await this.getQuoteToEdit(productConfiguration);
        this.setReviewAlert(quote);
        this.setState({ copiedQuote: quote });
        const dealerBranch = await DealerBranch.getById(
          quote.step1.dealerBranchId
        );
        const dealerBranchSummary = await DealerBranch.search(
          new DealerBranchFilter({ dealerBranchId: quote.step1.dealerBranchId })
        );
        const contactData = dealerBranchSummary[0].territoryManager;
        const machines = await Machine.search(
          new MachineFilter({
            carrierId: quote.step1.carrierId,
            weightCategoryId: quote.step1.weightCategoryId,
          })
        );

        const quoteProductConfiguration =
          await QuoteProductConfiguration.getProductConfigurationFromQuote(
            quote
          );

        await this.loadSharedDocuments(quote);
        await this.refreshDiscounts(quote);

        this.setState({
          contactData,
          quote,
          dealerBranch,
          machines,
          productConfiguration,
          quoteProductConfiguration
        });
      } else {
        this.setState({ productConfiguration }, async () => {
          if (window.inventoryQuoteDataViewModel?.quoteStep1Data) {
            const step1Data = new CreateQuoteStep1({ ...window.inventoryQuoteDataViewModel.quoteStep1Data });
            const machines = await Machine.search(
              new MachineFilter({
                carrierId: step1Data.carrierId,
                weightCategoryId: step1Data.weightCategoryId,
              })
            );

            this.setState({ machines });
            this.completeStep1(step1Data)
          } else {
            if (this.getStep() !== steps.step1) {
              this.setStep(steps.step1);
            }
          }
        });
      }

      this.setState({ isLoading: false });
    }

    loadSharedDocuments = async (quote) => {
      const productIds = quote.products.map((x) => x.productId);
      const productOptions = quote.products
        .map((x) => x.productOptions)
        .reduce(function (x, y) {
          return x.concat(y);
        }, []);
      const productOptionIds = productOptions.map((z) => z.productOptionId);
      const couplerStyleIds = quote.attachmentPoints.map(
        (x) => x.couplerStyleId
      );
      const sharedDocuments = await SharedDocument.sharedDocumentsforQuote(
        new SharedDocumentForQuoteFilter({
          productIds: productIds,
          productOptionIds: productOptionIds,
          couplerStyleIds: couplerStyleIds.filter((x) => x !== undefined),
        })
      );

      this.setState({ sharedDocuments });
    };

    async refreshDiscounts(quote) {
      this.setState({ showPricingSpinner: true });
      const payload = {
        quoteId: quote.id,
        useLatestDiscounts: quote.useLatestDiscounts,
        dealerBranchId: quote.step1.dealerBranchId,
        priceZoneId: quote.step1.priceZoneId,
        carrierId: quote.step1.carrierId,
        attachmentPoints: quote.attachmentPoints,
        products: quote.products,
        customLineItems: quote.customLineItems,
        optInDiscounts: quote.optInDiscounts
      };

      const response = await quotePricingService
        .calculatePricing(payload)
        .finally(() => this.setState({ showPricingSpinner: false }));

      quote.updateProductsPricing(response.products);
      quote.updateCustomLineItemsPricing(response.customLineItems);
      this.setState({ optInDiscounts: response.optInDiscounts, quote: quote });
    }

    getQuoteToEdit = async () => {
      const quote = await Quote.get(this.getEditingQuoteId());
      if (quote.installation.included)
        await this.setQuoteWithItsInstallationInformation(quote);
      else await this.setCurrentInstallationInformation(quote);

      return quote;
    };

    getStep = () => this.props.match.params.step.toLowerCase();

    getEditingQuoteId = () => {
      if (this.props.mode !== quoteWizardMode.editMode)
        throw new Error('Current mode is not edit');
      return this.props.match.params.id;
    };

    setStep = (step, queryParameters) => {
      const getModeUrl = () => {
        switch (this.props.mode) {
          case quoteWizardMode.createMode:
            return 'quotes/create';
          case quoteWizardMode.editMode:
            return `quotes/edit/${this.getEditingQuoteId()}`;
          case quoteWizardMode.quickPriceLookupMode:
          case quoteWizardMode.amiPriceLookupMode:
            return 'quick-price-lookup';
          default:
            return null;
        }
      };
      const queryString = !queryParameters
        ? ''
        : toQueryString(queryParameters);

      const redirect = () => {
        this.props.history.push(`/${getModeUrl()}/${step}?${queryString}`);
        if (this.state.preventPrompt) {
          this.setState({ preventPrompt: false });
        }
      };

      if (
        this.getStep() === steps.step3 &&
        this.state.step3SelectionInProgress
      ) {
        redirect();
      } else {
        this.setState({ preventPrompt: true }, () => redirect());
      }
    };

    setContact = (contactData) => {
      this.setState({ contactData: contactData });
    };

    completeStep1 = async (step1Data) => {
      this.setState({ submitting: true });
      const quote = new Quote();
      quote.setStep1(step1Data);

      if (step1Data.type === quoteCreationType.byWeightClass) {
        quote.setWeightCategoryId(step1Data.weightCategoryId);
      } else {
        try {
          const machine = await quote.validateStep1();
          quote.setMachine(machine);
        } catch (e) {
          this.setState({ submitting: false });
          throw e;
        }
      }

      const { dealerBranchSummary, machineId } = step1Data;
      if (dealerBranchSummary) {
        // This can be null when in AMI price lookup mode.
        quote.setPriceConfiguration(
          dealerBranchSummary.discountPercentage,
          dealerBranchSummary.taxRate
        );
      }

      // Dealer branch can be null when in AMI price lookup mode.
      const dealerBranch = step1Data.dealerBranchId
        ? await DealerBranch.getById(step1Data.dealerBranchId)
        : null;

      const quoteProductConfiguration =
        await QuoteProductConfiguration.getProductConfiguration(step1Data);

      quote.attachmentPoints = quoteProductConfiguration.attachmentPoints.map(
        (x) =>
          new QuoteAttachmentPoint(
            x.id,
            this.getMachineOemThumbMount(x, machineId)
          )
      );

      this.setState(
        {
          quote,
          dealerBranch,
          quoteProductConfiguration,
          submitting: false,
        },
        () => {
          this.setStep(steps.step2);
        }
      );
    };

    getMachineOemThumbMount(attachmentPoint, machineId) {
      const { machines } = this.state;
      const machineModel = machines.find((x) => x.id === machineId);

      var machineOemThumbMount = null;
      if (machineModel) {
        const attachmentPointConfiguration =
          machineModel.attachmentPointConfigurations.find(
            (x) => x.attachmentPointId === attachmentPoint.id
          );
        if (
          attachmentPointConfiguration &&
          attachmentPointConfiguration.thumbConfiguration
        ) {
          if (
            attachmentPointConfiguration.thumbConfiguration.oemThumbMount ===
            OemThumbMountType.NeverHasOemThumbMount
          ) {
            machineOemThumbMount = 'DoesNotHaveThumbMount';
          } else if (
            attachmentPointConfiguration.thumbConfiguration.oemThumbMount ===
            OemThumbMountType.AlwaysHasOemThumbMount
          ) {
            machineOemThumbMount = 'HasThumbMount';
          }
        }
      }

      return machineOemThumbMount;
    }

    cancelStep2 = () => {
      this.setStep(steps.step1);
    };

    cancelStep3 = () => {
      this.setStep(steps.step2);
    };

    completeStep2 = async () => {
      this.setStep(steps.step3);
    };

    completeStep3 = async (preparingForReview) => {
      if (this.props.mode === quoteWizardMode.amiPriceLookupMode) {
        this.setStep(steps.step1);
        return;
      }
      if (preparingForReview) {
        this.setState({ preparingForReview });
      }
      const quote = this.state.quote.clone();
      await this.setCurrentInstallationInformation(quote);
      await this.setAttachmentPointsCouplerInformation(quote);
      await this.loadSharedDocuments(quote);

      this.setState(
        {
          quote,
          step3SelectionInProgress: false,
          preparingForReview: false,
          submitting: false,
        },
        () => this.setStep(steps.review)
      );
    };

    updateProduct = (quoteProductDto) => {
      this.setState({ submitting: true });
      const quote = this.state.quote.clone();
      const step1 = this.state.quote.step1;
      const product = new QuoteProduct({
        dealerBranchId: step1.dealerBranchId,
        carrierId: step1.carrierId,
        weightCategoryId: step1.weightCategoryId,
        ...quoteProductDto,
      });
      this.extendQuoteProductInformation(product);
      this.setReviewAlertOnUpdate(product);
      quote.updateProduct(product);
      quote.maskPricingAndLeadTimes = this.hasManufacturingInstructionsReview(quote);
      this.setState({ quote, step3SelectionInProgress: false }, async () => {
        await this.completeStep3(false);
        await this.loadSharedDocuments(quote);
        NotificationManager.success('Product updated');
      });
    };

    hasManufacturingInstructionsReview = (quote) => {
      const productsWithManufacturingInstructions = quote?.products.filter(x => x.manufacturingInstructions.trim().length > 0 && x.requiresReview && !x.isReviewed) || [];
      return productsWithManufacturingInstructions.length > 0;
    }
    setReviewAlertOnUpdate = (quoteProduct) => {
      const { copiedQuote } = this.state;
      const product = copiedQuote?.products.find((x) => x.id === quoteProduct.id);
      const showReviewAlert = (quoteProduct?.manufacturingInstructions.trim().length > 0 && product?.manufacturingInstructions?.trim() !== quoteProduct?.manufacturingInstructions.trim());
      this.setState({ showReviewAlert });

    }
    extendQuoteProductInformation = (quoteProduct) => {
      const { quoteProductConfiguration } = this.state;
      const product = quoteProductConfiguration.products.find(
        (x) => x.id === quoteProduct.productId
      );

      const productGroup = quoteProductConfiguration.productGroups.find(
        (x) => x.id === quoteProduct.productGroupId
      );

      quoteProduct.partNumber = product.partNumber;
      quoteProduct.groupImageUrl = productGroup.imageUrl;
      quoteProduct.groupName = productGroup.name;
      quoteProduct.groupDescription = productGroup.description;
      quoteProduct.groupMarketing = productGroup.marketing;
      quoteProduct.groupNotesField = productGroup.notesField;
      quoteProduct.groupType = productGroup.type;
    };

    addProduct = async ({ quoteProductDto, onProductAdded }) => {
      this.setState({ submitting: true });
      const step1 = this.state.quote.step1;
      const product = new QuoteProduct({
        dealerBranchId: step1.dealerBranchId,
        carrierId: step1.carrierId,
        weightCategoryId: step1.weightCategoryId,
        ...quoteProductDto,
      });
      await this.state.quote.validateProduct(product).catch((e) => {
        this.setState({ submitting: false });
        throw e;
      });
      this.extendQuoteProductInformation(product);
      const quote = this.state.quote.clone();
      quote.addProduct(product);
      this.setReviewAlert(quote);
      quote.maskPricingAndLeadTimes = this.hasManufacturingInstructionsReview(quote);
      this.setState({ quote, step3SelectionInProgress: false }, async () => {
        NotificationManager.success('Product added');
        onProductAdded();
        await this.loadSharedDocuments(quote);
        await this.refreshDiscounts(quote);
        this.setState({ submitting: false });
        window.scrollTo(0, 0);
      });
    };
    setReviewAlert = (quote) => {
      if (!quote)
        return;

      const showReviewAlert = (quote.products.some(p => p.manufacturingInstructions?.trim().length > 0)) && quote.hasOpenCustomInstructionTask;
      this.setState({ showReviewAlert });
    }
    removeProduct = async (productId) => {
      const copy = this.state.quote.clone();
      copy.removeProduct(productId);
      this.setReviewAlert(copy);
      await this.setCurrentInstallationInformation(copy);
      await this.setAttachmentPointsCouplerInformation(copy);
      this.setState({ quote: copy }, async () => {
        await this.loadSharedDocuments(copy);
        await this.refreshDiscounts(copy);
      });
    };

    async setCurrentInstallationInformation(quote) {
      const installationInformation =
        await QuoteInstallationInformation.getData(
          new QuoteInstallationQuery(quote)
        );
      quote.setInstallationInformation(installationInformation);
    }

    async setQuoteWithItsInstallationInformation(quote) {
      const installationInformation =
        await QuoteInstallationInformation.getData(
          new QuoteInstallationQuery(quote),
          quote.id
        );
      quote.setInstallationInformation(installationInformation);
    }

    async setAttachmentPointsCouplerInformation(quote) {
      const info = await QuoteAttachmentPointCouplerInformation.calculateForSet(
        quote.products.map((x) => x.productId)
      );
      quote.updateAttachmentPointsCouplerInformation(info);
    }

    updateQuote = async (quote, getQuoteProductPricing) => {
      this.setState({ quote }, async () => {
        if (getQuoteProductPricing) await this.refreshDiscounts(quote);
      });
    };

    finishQuoteReview = () => {
      switch (this.props.mode) {
        case quoteWizardMode.createMode:
        case quoteWizardMode.quickPriceLookupMode:
          return this.saveQuote((q) => q.create());
        case quoteWizardMode.editMode:
          return this.saveQuote((q) => q.update());
        default:
          throw new Error('Invalid operation');
      }
    };

    navigateToQuotesDetails = (quoteId) =>
      this.props.history.push('/quotes/view/' + quoteId);

    navigateToDashboard = () => this.props.history.push('/dashboard');

    saveQuote = (saveOperation) => {
      this.setState({ submitting: true, preventPrompt: true });
      saveOperation(this.state.quote)
        .then((quoteId) => this.navigateToQuotesDetails(quoteId))
        .catch(() =>
          this.setState({ submitting: false, preventPrompt: false })
        );
    };

    onDownload = (document) => {
      this.setState(
        { submitting: true, documentIdDownloading: document.sharedDocumentId },
        () => {
          SharedDocument.download(
            document.sharedDocumentId,
            document.currentVersion
          ).finally(() =>
            this.setState({ submitting: false, documentIdDownloading: null })
          );
        }
      );
    };

    render() {
      const {
        contactData,
        quote,
        context,
        productConfiguration,
        quoteProductConfiguration,
        submitting,
        preventPrompt,
        preparingForReview,
        sharedDocuments,
        documentIdDownloading,
        optInDiscounts,
        showPricingSpinner,
        showReviewAlert,
        isLoading
      } = this.state;

      if (!productConfiguration || isLoading) {
        return (
          <div className="container text-center">
            <h4 className="mt-5 mb-2">Loading</h4>
            <Spinner show={true} size="fa-5x" />
          </div>
        );
      }

      const { mode } = this.props;
      const anyChanges =
        !preventPrompt &&
        mode !== quoteWizardMode.amiPriceLookupMode &&
        (this.getStep() === steps.review ||
          this.getStep() === steps.step3 ||
          this.getStep() === steps.step2);

      return (
        <QuoteWizardComponent
          areThereAnyChanges={anyChanges && !submitting}
          contact={contactData}
          quote={quote}
          context={context}
          productConfiguration={productConfiguration}
          quoteProductConfiguration={quoteProductConfiguration}
          completeStep1={this.completeStep1}
          completeStep2={this.completeStep2}
          completeStep3={this.completeStep3}
          cancelStep2={this.cancelStep2}
          step={this.getStep()}
          setStep={this.setStep}
          addProduct={this.addProduct}
          updateProduct={this.updateProduct}
          navigateToQuotesDetails={this.navigateToQuotesDetails}
          navigateToDashboard={this.navigateToDashboard}
          finishQuoteReview={this.finishQuoteReview}
          updateQuote={this.updateQuote}
          removeProduct={this.removeProduct}
          setContact={this.setContact}
          mode={mode}
          isEditMode={mode === quoteWizardMode.editMode}
          submitting={submitting}
          markStep3SelectionInProgress={(step3SelectionInProgress) =>
            this.setState({ step3SelectionInProgress })
          }
          preparingForReview={preparingForReview}
          sharedDocuments={sharedDocuments}
          onDownload={this.onDownload}
          documentIdDownloading={documentIdDownloading}
          onloadSharedDocuments={this.loadSharedDocuments}
          optInDiscounts={optInDiscounts}
          showPricingSpinner={showPricingSpinner}
          showReviewAlert={showReviewAlert}
        />
      );
    }
  }
);

const CreateQuoteWizard = () => (
  <QuoteWizardContainer mode={quoteWizardMode.createMode} />
);

const EditQuoteWizard = () => (
  <QuoteWizardContainer mode={quoteWizardMode.editMode} />
);

const PriceLookupQuoteWizard = () => (
  <QuoteWizardContainer mode={quoteWizardMode.quickPriceLookupMode} />
);

const AmiPriceLookupQuoteWizard = () => (
  <QuoteWizardContainer mode={quoteWizardMode.amiPriceLookupMode} />
);

export {
  CreateQuoteWizard,
  EditQuoteWizard,
  PriceLookupQuoteWizard,
  AmiPriceLookupQuoteWizard,
  quoteWizardMode,
};
